import { Box } from '@mui/material';

import {
  homeownerItems,
  selfServeHomeownerItems,
  messagesItem,
  tasksDrawerItem,
  toolsItems,
} from './navigation/navItems';
import {
  CollapsedNavItem,
  ExpandedNavGroup,
  NavDivider,
  SubNavItem,
} from './navigation/NavPrimitives';
import { useDueDiligenceNavItems } from './navigation/useDueDiligenceNavItems';
import { PropertyAdvisors } from './PropertyAdvisors';
import { PropertySelector } from './PropertySelector';
import { SidebarProcessingIndicator } from './SidebarProcessingIndicator';
import { SidebarShell } from './SidebarShell';
import { UserSwitcher } from './UserSwitcher';

import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

const disableItems = (items: SubNavItem[]): SubNavItem[] =>
  items.map((item) => ({ ...item, disabled: true }));

const lockItems = (items: SubNavItem[]): SubNavItem[] =>
  items.map((item) => ({ ...item, disabled: true, lock: true }));

export const ClientSidebar = () => {
  const { isLeftSidebarCollapsed, openTasksDrawer, openInbox } = useDashboardLayout();
  const { totalUnread } = useUnreadMessages();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { properties, selectedProperty, isInitialFetchingProperties, isFetchingProperties } =
    usePropertiesContext();
  const { conditionScore, isProcessing: isEqProcessing } = useHauserEQResults(
    selectedProperty?.property.id,
  );

  // Owner mode is per-selected-property: a buyer who closed on one home
  // but is still shopping for another sees owner nav on the owned
  // property and buyer nav on the consideration property.
  const isOwnerMode = selectedProperty?.isOwned ?? false;
  const isIndependentOwner = isOwnerMode && !isLoadingGroups && groups.length === 0;

  const hasNoProperties =
    !isInitialFetchingProperties &&
    !isFetchingProperties &&
    Array.isArray(properties) &&
    properties.length === 0;

  const manageItems = [
    ...(!isOwnerMode ? [tasksDrawerItem(openTasksDrawer)] : []),
    messagesItem(openInbox, totalUnread || undefined),
  ];
  const dueDiligenceItems = useDueDiligenceNavItems();

  /**
   * Section order depends on whether the selected property is owned:
   *
   *   Consideration property (isOwned=false):
   *     1. Manage (Tasks, Messages)
   *     2. Due Diligence
   *     3. Transaction
   *     4. Homeowner (locked — preview only)
   *     5. Tools
   *
   *   Owned property (isOwned=true):
   *     1. Homeowner             ← daily-driver after transfer
   *     2. Tools                 ← homeowner-adjacent utility, promoted
   *     3. Support (Messages)    ← "Manage" → "Support" relabel
   *     (Due Diligence + Transaction are pre-purchase activities —
   *      hidden entirely. Routes still resolve via direct URL.)
   *
   * Keying off `selectedProperty.isOwned` (not the user-global
   * `hasOwnedProperties` flag) means a buyer who closed on one home and
   * is shopping for another keeps full Due Diligence nav for the
   * consideration property — only the owned property's view switches.
   */
  type SectionKey = 'homeowner' | 'manage' | 'tools' | 'dueDiligence' | 'transaction';
  const ownerOrder: SectionKey[] = ['homeowner', 'tools', 'manage'];
  const buyerOrder: SectionKey[] = ['manage', 'dueDiligence', 'homeowner', 'tools'];
  const sectionOrder = isOwnerMode ? ownerOrder : buyerOrder;
  const visibleSectionOrder = isIndependentOwner
    ? sectionOrder.filter((section) => section !== 'manage')
    : sectionOrder;

  const itemsForSection = (key: SectionKey): SubNavItem[] => {
    switch (key) {
      case 'homeowner':
        if (!isOwnerMode) return lockItems(homeownerItems);
        if (!isLoadingGroups && groups.length === 0) {
          return selfServeHomeownerItems.map((item) =>
            item.path === '/dashboard/home-hub/repair-plan' && conditionScore == null
              ? { ...item, disabled: true, lock: !isEqProcessing, processing: isEqProcessing }
              : item,
          );
        }
        return homeownerItems;
      case 'manage':
        return hasNoProperties ? disableItems(manageItems) : manageItems;
      case 'tools':
        return hasNoProperties ? disableItems(toolsItems) : toolsItems;
      case 'dueDiligence':
        return hasNoProperties ? disableItems(dueDiligenceItems) : dueDiligenceItems;
      case 'transaction':
        return [];
      default:
        return [];
    }
  };

  const labelForSection: Record<SectionKey, string> = {
    homeowner: 'Homeowner',
    // "Manage" reads pre-purchase (buyer is managing the search /
    // transaction); post-purchase the same item set (just Messages
    // today) is better framed as "Support" — owners reach out to
    // the team rather than manage their pipeline.
    manage: isOwnerMode ? 'Support' : 'Manage',
    tools: 'Tools',
    dueDiligence: 'Due Diligence',
    transaction: 'Transaction',
  };

  if (isLeftSidebarCollapsed) {
    return (
      <SidebarShell
        topContent={
          <>
            <PropertySelector collapsed />
            <Box
              sx={{
                mx: '10px',
                mt: '4px',
                mb: '15px',
                borderTop: '1px solid var(--tx-line)',
                width: 'calc(100% - 20px)',
              }}
            />
          </>
        }
        footer={
          <>
            <Box
              sx={{
                mx: '10px',
                mt: '8px',
                mb: '4px',
                borderTop: '1px solid var(--tx-line)',
              }}
            />
            <UserSwitcher collapsed showEngagement={!isOwnerMode} />
          </>
        }>
        {visibleSectionOrder.map((sectionKey, sectionIdx) => (
          <Box key={sectionKey} sx={{ display: 'contents' }}>
            {itemsForSection(sectionKey).map((item) => (
              <CollapsedNavItem
                key={item.path}
                path={item.path}
                label={item.label}
                icon={item.icon}
                disabled={item.disabled}
                external={item.external}
                badge={item.badge}
                onClick={item.onClick}
              />
            ))}
            {sectionIdx < visibleSectionOrder.length - 1 && <NavDivider />}
          </Box>
        ))}
        <NavDivider />
      </SidebarShell>
    );
  }

  return (
    <SidebarShell
      topContent={
        <>
          {/* the property in context: one quiet tile holding the deal and property selector, the client and engagement */}
          <Box sx={{ position: 'relative', overflow: 'hidden', p: '4px 4px 6px', borderRadius: '16px', backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)', mb: '12px' }}>
            <PropertySelector inTile />
            {(!isOwnerMode || groups.length > 0) && (
              <>
                <Box sx={{ borderTop: '1px solid var(--tx-line)', mx: '10px' }} />
                <PropertyAdvisors showIndependentOwnerPrompt={false} inTile />
              </>
            )}
          </Box>
        </>
      }
      footer={
        <>
          <SidebarProcessingIndicator />
          {isIndependentOwner && (
            <Box sx={{ pb: '12px' }}>
              <PropertyAdvisors promptOnly />
            </Box>
          )}
          <Box sx={{ pb: '16px' }}>
            <UserSwitcher showEngagement={!isOwnerMode} />
          </Box>
        </>
      }>
      <Box
        data-testid="client-sidebar-nav"
        sx={{ display: 'flex', flexDirection: 'column', px: 0, py: 0, mt: '10px' }}>
        {visibleSectionOrder.map((sectionKey) => (
          <ExpandedNavGroup
            key={sectionKey}
            label={labelForSection[sectionKey]}
            items={itemsForSection(sectionKey)}
            collapsible={sectionKey === 'transaction'}
            defaultCollapsed={sectionKey === 'transaction'}
          />
        ))}
      </Box>
    </SidebarShell>
  );
};
